import { Typography } from '@mui/material';
import { isEmpty, size } from 'es-toolkit/compat';
import React from 'react';

import { typeToName } from 'components/ReviewPanel/form.util';
import { ResultsMode } from 'interfaces/experimentResults';
import { ReviewForm } from 'interfaces/reviewForm';
import { exportFormResults, exportProcedures, exportQcResults } from 'utils/exports';
import { useFormatBracketsOptions } from 'utils/formatBrackets/formatBracketsOptions';
import { useStudyStainTypeIds } from 'utils/queryHooks/stainType/useStudyStainTypes';
import { ExportOutputChoices, ExportScopeField, ExportScopeFields } from './ExportFormFields';
import { ExportFormShell } from './ExportFormShell';
import { getCasesFieldLabel, getScopeOptionLabels } from './exportScopeLabels';
import { SeparateOutputsStatus } from './SeparateOutputsStatus';
import { ResultsExportContext } from './useResultsExportContext';
import { useResultsExportUrlState } from 'services/export/useResultsExportUrlState';
import { StartExport, useSeparateOutputExports } from 'services/export/useSeparateOutputExports';

type MetadataScope = 'filtered' | 'whole';

interface MetadataExportFormProps {
  context: ResultsExportContext;
  forms?: ReviewForm[];
  skipPreloadingStudyStains?: boolean;
  startExport: StartExport;
  isExportRunning: boolean;
  onBack: () => void;
}

const DATASET_METADATA_ID = 'dataset-metadata';
const QC_LABELS_ID = 'qc-labels';

export const MetadataExportForm: React.FC<MetadataExportFormProps> = ({
  context,
  forms,
  skipPreloadingStudyStains,
  startExport,
  isExportRunning,
  onBack,
}) => {
  const { study, studyId, labId, resultsMode, orchestrationId } = context;

  const { query, update } = useResultsExportUrlState();
  const scope: MetadataScope =
    context.areFiltersAppliedExceptStudy && query.metadataExportScope !== 'whole' ? 'filtered' : 'whole';
  const [selected, setSelected] = React.useState<Record<string, boolean>>({ [DATASET_METADATA_ID]: true });

  const { data: stainTypesInStudy, isLoading: isLoadingStainTypes } = useStudyStainTypeIds(studyId, {
    enabled: Boolean(studyId) && !skipPreloadingStudyStains,
  });
  const formatBracketsOptions = useFormatBracketsOptions({ addStain: size(stainTypesInStudy) > 1, studyId });

  const hasQcLabels = !isEmpty(study?.qcLabelsConfig);
  const isManualWithoutOrchestration = resultsMode === ResultsMode.Manual && !orchestrationId;
  const qcLabelsBlockedReason = !hasQcLabels
    ? 'No QC labels are configured for this study.'
    : isManualWithoutOrchestration
    ? 'Select an orchestration to export label results.'
    : '';
  const formsToOffer = context.canViewReviewMenu ? forms ?? [] : [];

  const allOutputs = [
    {
      id: DATASET_METADATA_ID,
      title: 'Dataset metadata',
      run: (signal: AbortSignal) =>
        exportProcedures(
          studyId as string,
          scope === 'filtered'
            ? context.generateEncodedParams({ skipPagination: true })
            : context.generateEncodedParams({ skipPagination: true, ...context.emptyFiltersExceptStudy }),
          signal
        ),
    },
    ...(context.canViewInternalLabels
      ? [
          {
            id: QC_LABELS_ID,
            title: 'Label results',
            run: (signal: AbortSignal) =>
              exportQcResults({
                labId: labId as string,
                studyId: studyId as string,
                context: {
                  ...formatBracketsOptions,
                  orchestrationId,
                  skipPreloadingStudyStains: isLoadingStainTypes || skipPreloadingStudyStains,
                  labId,
                  studyId,
                },
                resultsMode,
                ...(resultsMode === ResultsMode.Manual && { orchestrationId }),
                signal,
              }),
          },
        ]
      : []),
    ...formsToOffer.map((form) => ({
      id: `form-${form.id}`,
      title: `${typeToName(form.type)} responses`,
      run: (signal: AbortSignal) => exportFormResults(form.id, signal),
    })),
  ];

  const blockedReasonById: Record<string, string> = { [QC_LABELS_ID]: qcLabelsBlockedReason };
  const outputs = allOutputs.filter(
    (output) => (allOutputs.length === 1 || selected[output.id]) && !blockedReasonById[output.id]
  );

  const { outcomes, exportOutputs, retryFailedOutputs, hasRetryableOutputs } = useSeparateOutputExports(startExport);

  const guidance = isEmpty(outputs) ? 'Select at least one output.' : '';

  return (
    <ExportFormShell
      study={study}
      scope={
        <ExportScopeFields>
          <ExportScopeField
            id="metadata-cases"
            label={getCasesFieldLabel(context.isSlidesMode)}
            options={[
              ...(context.areFiltersAppliedExceptStudy
                ? [{ value: 'filtered', label: getScopeOptionLabels(context.isSlidesMode).filtered }]
                : []),
              { value: 'whole', label: getScopeOptionLabels(context.isSlidesMode).whole },
            ]}
            value={scope}
            onChange={(value) => update({ metadataExportScope: value })}
          />
          <Typography variant="body2" color="text.secondary">
            The scope applies to dataset metadata. Label results and form responses always cover their whole study or
            form; selected and excluded cases are not applied to them.
          </Typography>
        </ExportScopeFields>
      }
      options={
        <ExportOutputChoices
          label="Outputs"
          outputs={allOutputs.map((output) => ({
            id: output.id,
            label: `${output.title} (.csv)`,
            // A single output is fixed, so it is always selected.
            checked:
              allOutputs.length === 1
                ? !blockedReasonById[output.id]
                : Boolean(selected[output.id]) && !blockedReasonById[output.id],
            disabled: Boolean(blockedReasonById[output.id]),
            note: blockedReasonById[output.id] || undefined,
          }))}
          onChange={(id, checked) => setSelected((current) => ({ ...current, [id]: checked }))}
        />
      }
      status={
        <SeparateOutputsStatus
          outputs={outputs}
          outcomes={outcomes}
          canRetry={hasRetryableOutputs && !isExportRunning}
          onRetry={() => void retryFailedOutputs()}
        />
      }
      primaryLabel="Download"
      primaryDisabled={Boolean(guidance) || isExportRunning}
      primaryGuidance={guidance}
      onPrimary={() => void exportOutputs(outputs)}
      onBack={onBack}
    />
  );
};
